import { Tooltip } from '@mui/material'
import { LatestQueueItem, OptJobType } from 'api/optJobs/optJobs.api'
import { t } from 'i18next'
import React, { ReactElement, useCallback } from 'react'
import PublishRocketIcon from '../PublishRocketIcon/PublishRocketIcon'
import styles from './LatestPublishedOptimizationInfoBox.module.less'
import { useLatestQueueItemsInfo, useTimeSincePublished } from './latestPublishedInfoBoxHooks'

type LatestPublishedOptimizationInfoBoxProps = {
  type: OptJobType
  isVisible: boolean
}

export default function LatestPublishedOptimizationInfoBox({
  type,
  isVisible,
}: LatestPublishedOptimizationInfoBoxProps): ReactElement {
  const { subtypes, latestQueueItemData, status: latestQueueItemStatus } = useLatestQueueItemsInfo({ type })

  if (!isVisible || latestQueueItemStatus === 'pending') {
    return <></>
  }

  if (subtypes.length > 0) {
    return (
      <div className={styles.LatestPublishedOptimizationInfoBox_Container}>
        {subtypes.map((subtype) => {
          const queueItemForSubtype = latestQueueItemData?.find((item) => item.subtype === subtype.subtype)
          return (
            <React.Fragment key={subtype.subtype}>
              {queueItemForSubtype && (
                <LatestPublishedItem key={subtype.subtype} subtype={subtype} latestQueueItem={queueItemForSubtype} />
              )}
              {!queueItemForSubtype && <LatestPublishedItemNotFound subtype={subtype} />}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  return (
    <div className={styles.LatestPublishedOptimizationInfoBox_Container}>
      {latestQueueItemData?.[0] && <LatestPublishedItem latestQueueItem={latestQueueItemData[0]} />}
      {!latestQueueItemData?.[0] && <LatestPublishedItemNotFound />}
    </div>
  )
}

function LatestPublishedItem({
  latestQueueItem,
  subtype,
}: {
  latestQueueItem: LatestQueueItem | null
  subtype?: Subtype
}): ReactElement {
  const timeSincePublished = useTimeSincePublished({ latestQueueItem })

  const explainingTooltip = useCallback((timeAgo: string | null, creatorName: string, subtype?: Subtype) => {
    if (creatorName) {
      return t('{{subtype}} was last published {{timeSincePublished}} ago by {{publishedBy}}', {
        subtype: subtype?.subtype_display_name ?? t('Production plan'),
        timeSincePublished: timeAgo,
        publishedBy: creatorName,
      })
    }
    return t('{{subtype}} was last published {{timeSincePublished}} ago', {
      subtype: subtype?.subtype_display_name ?? t('Production plan'),
      timeSincePublished: timeAgo,
    })
  }, [])

  if (!timeSincePublished) {
    return <></>
  }
  const infoText = t(`{{subtype}}: {{timeSincePublished}} ago`, {
    subtype: subtype?.subtype_display_name ?? t('Last published'),
    timeSincePublished: timeSincePublished,
  })

  return (
    <Tooltip title={explainingTooltip(timeSincePublished, latestQueueItem?.created_by ?? '', subtype)} arrow>
      <div className={styles.LatestPublishedOptimizationInfoBox_Item}>
        <PublishRocketIcon status="active" />
        {infoText}
      </div>
    </Tooltip>
  )
}

function LatestPublishedItemNotFound({ subtype }: { subtype?: Subtype }): ReactElement {
  if (subtype) {
    const subTypeInfoText = t(`{{subtype}}: No production plan has been published`, {
      subtype: subtype?.subtype_display_name ?? t('Last published'),
    })
    return (
      <div className={styles.LatestPublishedOptimizationInfoBox_Item}>
        <PublishRocketIcon status="inactive" />
        {subTypeInfoText}
      </div>
    )
  }

  const infoText = t(`No production plan has been published`)
  return (
    <div className={styles.LatestPublishedOptimizationInfoBox_Item}>
      <PublishRocketIcon status="inactive" />
      {infoText}
    </div>
  )
}
